import { useQuery } from '@tanstack/react-query';
import { Link } from 'react-router';
import { Alert } from '../components/ui/Alert';
import { Card } from '../components/ui/Card';
import { MockBadge } from '../components/MockBadge';
import { apiFetch } from '../lib/api';
import { formatMoney } from '../lib/format';
import type { CouponOverview } from '../types/api';

type Discount = CouponOverview['shared'][number]['discounts'][number];

function sumByCurrency(list: readonly Discount[]): Discount[] {
  const sums = new Map<string, number>();
  for (const d of list) sums.set(d.currency, (sums.get(d.currency) ?? 0) + d.amount);
  return [...sums]
    .map(([currency, amount]) => ({ currency, amount }))
    .sort((a, b) => b.amount - a.amount);
}

/** "€5,646.40", or "€96.40 + DKK 120.00" when orders were paid in more than one currency. */
function money(list: readonly Discount[]): string {
  if (list.length === 0) return '—';
  return list.map((d) => formatMoney(d.amount, d.currency, 2)).join(' + ');
}

/** Read-only coupon overview built from existing data (no shop call, nothing is written). */
export function CouponsPage() {
  const { data, isLoading, isError } = useQuery({
    queryKey: ['coupons'],
    queryFn: ({ signal }) => apiFetch<CouponOverview>('/coupons', { signal }),
  });
  const sharedUses = data?.shared.reduce((n, s) => n + s.orders, 0) ?? 0;
  const sharedTotals = sumByCurrency(data?.shared.flatMap((s) => s.discounts) ?? []);

  return (
    <>
      <header className="flex items-center gap-3">
        <h1 className="m-0 text-2xl font-semibold tracking-tight">Coupons</h1>
        <MockBadge />
      </header>
      <p className="m-0 text-sm text-muted">
        Personal single-use codes made by campaigns, and shared codes customers used in orders.
      </p>
      {isLoading ? <div className="text-muted">Loading…</div> : null}
      {isError ? <Alert>The coupons could not be loaded. Please try again.</Alert> : null}
      {data ? (
        <>
          <Card className="p-4 flex flex-col gap-3">
            <h2 className="m-0 text-sm font-semibold">Personal codes per campaign</h2>
            {data.personal.length === 0 ? (
              <p className="m-0 text-sm text-muted">No campaign has issued personal codes yet.</p>
            ) : (
              <table className="w-full text-sm border-collapse">
                <thead>
                  <tr className="text-left text-muted">
                    <th className="py-2 font-medium">Campaign</th>
                    <th className="py-2 font-medium">Discount</th>
                    <th className="py-2 font-medium text-right">Issued</th>
                    <th className="py-2 font-medium text-right">Redeemed</th>
                  </tr>
                </thead>
                <tbody>
                  {data.personal.map((p) => (
                    <tr key={p.campaignId} className="border-t border-border-soft">
                      <td className="py-2">
                        <Link to={`/kampagner/${p.campaignId}`} className="text-link">
                          {p.name}
                        </Link>
                      </td>
                      <td className="py-2">
                        {p.percent !== null ? `${p.percent}%` : '—'}
                        {p.prefix ? ` · ${p.prefix}` : ''}
                      </td>
                      <td className="py-2 text-right">{p.issued}</td>
                      <td className="py-2 text-right">{p.redeemed}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
            <p className="m-0 text-xs text-muted">
              Automations have issued {data.automationIssued} personal codes in total.
            </p>
            {data.redeemedCapped ? (
              <p className="m-0 text-xs text-muted">
                Redeemed counts cover only the most recent orders.
              </p>
            ) : null}
          </Card>

          <Card className="p-4 flex flex-col gap-3">
            <h2 className="m-0 text-sm font-semibold">Shared codes used in orders</h2>
            {data.shared.length === 0 ? (
              <p className="m-0 text-sm text-muted">No shared codes in the imported orders.</p>
            ) : (
              <>
                <p className="m-0 text-sm">
                  Shared codes were used {sharedUses.toLocaleString('en-GB')} times, with{' '}
                  <strong>{money(sharedTotals)}</strong> in discount in total.
                </p>
                <table className="w-full text-sm border-collapse tabular-nums">
                  <thead>
                    <tr className="text-left text-muted">
                      <th className="py-2 font-medium">Code</th>
                      <th className="py-2 font-medium text-right">Orders</th>
                      <th className="py-2 font-medium text-right">Discount given</th>
                      <th className="py-2 font-medium text-right">Per order</th>
                    </tr>
                  </thead>
                  <tbody>
                    {data.shared.map((s) => (
                      <tr key={s.code} className="border-t border-border-soft">
                        <td className="py-2">
                          <code className="text-[13px]">{s.code.toUpperCase()}</code>
                        </td>
                        <td className="py-2 text-right">{s.orders.toLocaleString('en-GB')}</td>
                        <td className="py-2 text-right">{money(s.discounts)}</td>
                        <td className="py-2 text-right text-muted">
                          {s.discounts.length === 1
                            ? formatMoney(
                                s.discounts[0]!.amount / s.orders,
                                s.discounts[0]!.currency,
                                2,
                              )
                            : '—'}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
                <p className="m-0 text-xs text-muted">
                  Amounts are in the currency the order was paid in. When an order used more than
                  one code, its discount is split evenly between them.
                </p>
              </>
            )}
            {data.ordersCapped ? (
              <p className="m-0 text-xs text-muted">
                Only the most recent orders with a code are counted.
              </p>
            ) : null}
          </Card>
        </>
      ) : null}
    </>
  );
}
